import { toggleQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { ReconnectingOverlay } from "components/scrapEyeVideoPlayer/ReconnectingOverlay";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTenantTranslation } from "hooks/formatters";
import { useHlsVideo } from "hooks/useHlsVideo";
import { useEffect, useMemo, useState } from "react";
import { useGetRadioGcsReadTokenQuery } from "src/store/api/generatedApi";
import { RadioChannelSelect } from "./RadioChannelSelect";
import { TranscriptScopeToggle } from "./TranscriptScopeToggle";
import { TranscriptView } from "./TranscriptView";
import {
  useLiveRadioUrl,
  useRadioReceivers,
  useRadioSegmentInfo,
  useRadioTranscript,
} from "./hooks";
import { useTranscriptSync } from "./transcriptSync";
import { useAudioActivity } from "./useAudioActivity";

dayjs.extend(relativeTime);

// Rolling transcript window behind the live edge, and how often we re-evaluate
// where its trailing edge sits so freshly transcribed segments appear.
const LIVE_TRANSCRIPT_LOOKBACK_MINUTES = 3;
const LIVE_TRANSCRIPT_REFRESH_MS = 5_000;
// Snap the window edge to a coarse step so ticks within a step produce the same
// RTK Query key and reuse one cache entry (concurrent live panels share it too),
// instead of every 5s tick re-fetching the whole lookback under a fresh key. The
// step stays well under the lookback, so captions still roll in promptly.
const LIVE_TRANSCRIPT_WINDOW_STEP_MS = 15_000;

const currentWindowEndMs = () =>
  Math.floor(Date.now() / LIVE_TRANSCRIPT_WINDOW_STEP_MS) *
  LIVE_TRANSCRIPT_WINDOW_STEP_MS;

const METER_BARS = 20;
const METER_BAR_IDS = Array.from(
  { length: METER_BARS },
  (_, index) => `meter-bar-${index}`
);
// The RMS level of decoded voice is small; scale so a normal transmission
// lights most of the meter without clipping to full on quiet speech.
const METER_GAIN = 4;
// Refresh the freshness read while the panel is open so the label keeps up with
// a receiver that goes quiet mid-session.
const SEGMENT_INFO_POLL_MS = 30_000;

type Props = Readonly<{
  deviceId: number;
}>;

export const LiveRadioPanel = ({ deviceId }: Props) => (
  // useHlsVideo (rendered by LiveRadioContent) calls useGcsToken, so the
  // consumer must live *inside* this radio-scoped provider — otherwise it
  // resolves the page-level video-scoped token and every radio/*.ts fetch 403s.
  <EnsureGcsTokenExists useTokenQuery={useGetRadioGcsReadTokenQuery}>
    <LiveRadioContent deviceId={deviceId} />
  </EnsureGcsTokenExists>
);

const LiveRadioContent = ({ deviceId }: Props) => {
  const { t } = useTenantTranslation();
  const { receivers, selectedReceiverId, selectReceiver, channelLabels } =
    useRadioReceivers(deviceId);
  const srcUrl = useLiveRadioUrl(deviceId, {
    receiverId: selectedReceiverId,
  });
  const [listening, setListening] = useState(false);
  const [allChannels, setAllChannels] = useNavigationQuery(
    "liveRadioAllChannels",
    false,
    toggleQuery,
    "replace"
  );

  // Only poll while listening: the staleness label is the query's only consumer
  // and it renders under the listening branch, so an idle tab must not cost a
  // request (two DB queries) every 30s.
  //
  // A segment only finalizes when the next one opens, so healthy lag is about
  // one segment length plus upload/ingest, and last_received_at itself trails
  // by up to one poll interval. Deriving the staleness cut from the segment
  // length (rather than a fixed 120s) stops the on-air indicator flapping at
  // longer, operator-configured lengths.
  const { segmentLengthSec, lastReceivedAt } = useRadioSegmentInfo(deviceId, {
    skip: !listening,
    pollingInterval: SEGMENT_INFO_POLL_MS,
    receiverId: selectedReceiverId,
  });
  const staleAfterSeconds = segmentLengthSec * 2 + SEGMENT_INFO_POLL_MS / 1000;
  const staleLabel =
    lastReceivedAt != null &&
    dayjs().diff(dayjs(lastReceivedAt), "second") > staleAfterSeconds
      ? t("liveRadioLastReceived", { when: dayjs(lastReceivedAt).fromNow() })
      : null;

  const { ref, fatalError, isReconnecting, autoplayBlocked, getPlayingDate } =
    useHlsVideo({
      srcUrl: listening ? (srcUrl ?? "") : "",
      isLive: true,
      autoPlay: listening,
    });

  // Slide the transcript window forward while listening so newly transcribed
  // segments roll in as captions. Snapping to a step means most ticks re-set the
  // same value, so React bails and the query key holds steady between steps.
  const [liveWindowEndMs, setLiveWindowEndMs] = useState(currentWindowEndMs);
  useEffect(() => {
    if (!listening) return;
    const intervalId = window.setInterval(() => {
      setLiveWindowEndMs(currentWindowEndMs());
    }, LIVE_TRANSCRIPT_REFRESH_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [listening]);
  const transcriptWindow = useMemo(
    () => ({
      startTime: dayjs(liveWindowEndMs)
        .subtract(LIVE_TRANSCRIPT_LOOKBACK_MINUTES, "minute")
        .toISOString(),
      endTime: dayjs(liveWindowEndMs).toISOString(),
    }),
    [liveWindowEndMs]
  );
  const transcript = useRadioTranscript({
    deviceId,
    receiverId: selectedReceiverId,
    allChannels,
    skip: !listening,
    ...transcriptWindow,
  });
  const { lines, activeWordKey } = useTranscriptSync(
    transcript,
    getPlayingDate,
    ref,
    {
      // Labelled whatever the scope, so a block always names the talkgroup it
      // transcribes and matches the clips filed under it.
      channelLabels,
      playingReceiverId: selectedReceiverId,
    }
  );

  const { litBars, active } = useAudioActivity(ref, listening, {
    barCount: METER_BARS,
    gain: METER_GAIN,
  });

  return (
    <Stack p={2} spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t("liveRadioDescription")}
      </Typography>

      <Box
        sx={{
          position: "relative",
          border: 1,
          borderColor: "grey.300",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant={listening ? "outlined" : "contained"}
            onClick={() => {
              setListening((value) => !value);
            }}
          >
            {listening ? t("liveRadioStop") : t("liveRadioListen")}
          </Button>

          <Box sx={{ minWidth: 148 }}>
            {!listening ? (
              <Typography variant="body2" color="text.secondary">
                {t("liveRadioIdle")}
              </Typography>
            ) : staleLabel ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: "grey.500",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {staleLabel}
                </Typography>
              </Stack>
            ) : active ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "error.main", fontWeight: "fontWeightBold" }}
                >
                  {t("liveRadioOnAir")}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("liveRadioMonitoring")}
              </Typography>
            )}
          </Box>

          <RadioChannelSelect
            receivers={receivers}
            selectedReceiverId={selectedReceiverId}
            onSelect={selectReceiver}
          />

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="flex-end"
            aria-hidden="true"
            sx={{ height: 28, flex: 1 }}
          >
            {METER_BAR_IDS.map((barId, index) => (
              <Box
                key={barId}
                sx={{
                  flex: 1,
                  height: `${20 + (index / METER_BARS) * 80}%`,
                  borderRadius: 0.5,
                  bgcolor: index < litBars ? "primary.main" : "grey.300",
                  transition: "background-color 60ms linear",
                }}
              />
            ))}
          </Stack>
        </Stack>

        {/* Live is a continuous stream, so no native transport/scrubber —
            the Listen toggle + meter is the UI; volume is the system's. */}
        <audio ref={ref} style={{ display: "none" }} />

        {isReconnecting ? <ReconnectingOverlay /> : null}
        {/* The browser blocks unmuted autoplay when the manifest resolves after
            the click's activation window; a fresh click re-primes playback. */}
        {listening && autoplayBlocked && !fatalError ? (
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              void ref.current?.play();
            }}
          >
            {t("liveRadioEnableSound")}
          </Button>
        ) : null}
        {fatalError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {t("radioPlaybackError")}
          </Alert>
        ) : null}
      </Box>

      {listening ? (
        <Stack spacing={1}>
          <TranscriptScopeToggle
            channelCount={receivers.length}
            allChannels={allChannels}
            onChange={setAllChannels}
          />
          <TranscriptView
            lines={lines}
            activeWordKey={activeWordKey}
            onChannelClick={selectReceiver}
          />
        </Stack>
      ) : null}
    </Stack>
  );
};
